/**
 * BundleBuilder
 * Lets a shopper pick multiple variants of the same product (e.g. colors)
 * with individual quantities, see a live running total and quantity-tier
 * discount, and add everything to the cart in a single request.
 *
 * The tier progress bar reflects TOTAL quantity of this product's variants
 * in the cart (existing cart quantity + what's currently selected in the
 * widget), not just the current selection - so it doesn't reset to 0 every
 * time the shopper adds to cart. The "Totalt / Add to cart" summary still
 * reflects only the current selection (what this click will add).
 *
 * NOTE: the discount shown here is for display only. It must be backed by
 * a matching Shopify automatic discount (minimum quantity of items on this
 * product) so the price is actually reduced at checkout. Keep the tier
 * thresholds/percentages in the block settings in sync with the real
 * discount rules in Shopify admin.
 *
 * VAT toggle integration (2026-07): variant.price (data-variant-price) is
 * always the shop's raw, tax-inclusive price - this class does all its own
 * quantity/tier/subtotal math in that raw currency, exactly as before. Only
 * at DISPLAY time (per-row unit price, running subtotal, Add-to-cart button
 * label) do we convert through window.CJVatToggle.computeDisplay(), if the
 * CrabJuice VAT toggle (assets/vat-toggle.js) is present on the page - falls
 * back to the raw/original formatting untouched if it isn't, so this file
 * works standalone in themes that don't have the VAT toggle at all.
 *
 * Cart-drawer integration (2026-07-21, corrected again): after actually
 * testing this store's live cart behavior in the browser - not just reading
 * the app's script - Zipify OCU's own drawer (.ocu-cart-drawer) does NOT
 * take over storefront add-to-cart interactions. A plain product-form add,
 * a manual click on the header cart icon, and this widget's own batch add
 * were all tested directly: every one of them left OCU's drawer closed
 * (visibility: hidden, positioned off-screen) and opened Dawn's native
 * cart-drawer instead, or (for this widget, while the previous OCU-aware
 * patch below was in place) opened nothing at all. The theory written here
 * on 2026-07-20 - that OCU reacts to the same DOM mutation renderContents()
 * produces and normally wins a rendering race against Dawn's native drawer -
 * was not confirmed by live testing and is now believed to be wrong.
 * Whatever OCU's drawer is for on this store, it is not a replacement for
 * Dawn's native cart-drawer during normal shopping.
 *
 * Given that, the OCU-aware branching that used to live in addToCart()
 * below (temporarily no-op'ing cart.open() whenever .ocu-cart-drawer
 * existed in the DOM) has been removed. It was actively harmful: since OCU
 * never visibly opens on this flow, the branch just suppressed Dawn's
 * native drawer with nothing to replace it, so nothing opened at all after
 * a bundle add while it was in place. This file now calls
 * cart.renderContents(data) unconditionally, matching every other product's
 * add-to-cart behavior on this site (see assets/product-form.js) exactly.
 *
 * The original "old cart pops up sometimes" report from David is still not
 * conclusively explained - unconditional renderContents()+open() is what
 * every normal product already does, so if the native drawer opening at all
 * is itself the "old cart" he doesn't want on this page, the real fix is a
 * product/UX decision (should this widget open a cart drawer at all?), not
 * a timing patch. Don't reintroduce OCU-detection logic without testing it
 * live first - two attempts at that already shipped broken (see project
 * memory: crabjuice_bundle_builder_knobs).
 */
class BundleBuilder {
  constructor(container) {
    this.container = container;
    this.tiers = this.parseTiers(container);
    this.progressFill = container.querySelector('[data-progress-fill]');
    this.tierEls = container.querySelectorAll('[data-tier]');
    this.totalQtyEl = container.querySelector('[data-total-qty]');
    this.subtotalEl = container.querySelector('[data-subtotal]');
    this.variantItems = container.querySelectorAll('[data-variant-item]');
    this.addButton = container.querySelector('[data-bundle-add]');
    this.buttonText = container.querySelector('[data-button-text]');
    this.cartQuantity = 0;

    this.init();
  }

  parseTiers(container) {
    const tiers = [{ qty: 1, discount: 0 }];
    for (let i = 2; i <= 5; i++) {
      const qty = parseInt(container.dataset[`tier${i}Qty`], 10);
      const discount = parseFloat(container.dataset[`tier${i}Discount`]);
      if (qty) tiers.push({ qty, discount: discount || 0 });
    }
    return tiers.sort((a, b) => a.qty - b.qty);
  }

  init() {
    this.variantItems.forEach((item) => this.setupQuantityHandlers(item));
    if (this.addButton) {
      this.addButton.addEventListener('click', () => this.addToCart());
    }
    // Re-render (per-row prices, subtotal, button label) whenever the customer
    // changes their VAT display choice elsewhere on the page (header pill or
    // popup), even if they don't touch any quantity control afterward.
    document.addEventListener('cj-vat:changed', () => this.updateBundle());

    // Render immediately with what we know synchronously, so per-row VAT
    // display is correct right away - then refine the tier bar once we know
    // how much of this product is already in the cart.
    this.updateBundle();
    this.syncCartQuantity().then(() => this.updateBundle());

    // Keep the cart-quantity baseline correct if the cart changes some other
    // way (a line removed in the drawer, another widget instance adding
    // stock, etc), not just through this widget's own Add to cart button.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.syncCartQuantity().then(() => this.updateBundle());
      });
    }
  }

  async syncCartQuantity() {
    try {
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const response = await fetch(`${cartUrl}.js`);
      const cart = await response.json();
      const variantIds = new Set(
        Array.from(this.variantItems).map((item) => parseInt(item.dataset.variantId, 10))
      );
      this.cartQuantity = (cart.items || [])
        .filter((line) => variantIds.has(line.variant_id))
        .reduce((sum, line) => sum + line.quantity, 0);
    } catch (e) {
      // Leave the last known value in place rather than resetting to 0 on
      // a transient network error.
    }
  }

  setupQuantityHandlers(item) {
    const input = item.querySelector('[data-qty-input]');
    const minusBtn = item.querySelector('[data-qty-minus]');
    const plusBtn = item.querySelector('[data-qty-plus]');
    const addBtn = item.querySelector('[data-variant-add]');
    const wrapper = item.querySelector('[data-quantity-wrapper]');
    const maxQty = parseInt(input.dataset.max, 10) || 999;

    addBtn.addEventListener('click', () => {
      input.value = 1;
      addBtn.style.display = 'none';
      wrapper.style.display = 'inline-flex';
      this.updateBundle();
    });

    plusBtn.addEventListener('click', () => {
      const current = parseInt(input.value, 10) || 0;
      if (current < maxQty) {
        input.value = current + 1;
        this.updateBundle();
      }
    });

    minusBtn.addEventListener('click', () => {
      const current = parseInt(input.value, 10) || 0;
      if (current > 1) {
        input.value = current - 1;
      } else {
        input.value = 0;
        wrapper.style.display = 'none';
        addBtn.style.display = 'inline-block';
      }
      this.updateBundle();
    });

    input.addEventListener('change', () => {
      let val = parseInt(input.value, 10) || 0;
      val = Math.max(0, Math.min(val, maxQty));
      input.value = val;
      if (val === 0) {
        wrapper.style.display = 'none';
        addBtn.style.display = 'inline-block';
      } else {
        wrapper.style.display = 'inline-flex';
        addBtn.style.display = 'none';
      }
      this.updateBundle();
    });
  }

  getVariantPrice(item) {
    return parseInt(item.dataset.variantPrice, 10) || 0;
  }

  /* Converts a raw (shop, tax-inclusive) cents value to whatever the customer
     has currently chosen to see (incl./excl. VAT, per their selected country) -
     via the CrabJuice VAT toggle's public API if present, otherwise a no-op. */
  getDisplayCents(rawCents) {
    if (window.CJVatToggle && typeof window.CJVatToggle.computeDisplay === 'function') {
      return window.CJVatToggle.computeDisplay(rawCents);
    }
    return rawCents;
  }

  getActiveTier(totalQty) {
    let active = this.tiers[0];
    this.tiers.forEach((tier) => {
      if (totalQty >= tier.qty) active = tier;
    });
    return active;
  }

  formatMoney(cents) {
    // Prefer the CrabJuice VAT toggle's own formatter + the shop's real
    // money_format, so this widget's numbers always look identical to every
    // other price on the page (and stay correct if money_format ever changes) -
    // fall back to a generic Intl formatter only when that isn't available.
    if (
      window.CJVatToggle &&
      typeof window.CJVatToggle.formatMoney === 'function' &&
      window.CJ_VAT &&
      window.CJ_VAT.moneyFormat
    ) {
      return window.CJVatToggle.formatMoney(cents, window.CJ_VAT.moneyFormat);
    }

    const amount = cents / 100;
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'SEK';
      return new Intl.NumberFormat(document.documentElement.lang || 'sv-SE', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  updateBundle() {
    const items = [];
    let totalQty = 0;
    let rawSubtotal = 0;

    this.variantItems.forEach((item) => {
      const input = item.querySelector('[data-qty-input]');
      const qty = parseInt(input.value, 10) || 0;
      const price = this.getVariantPrice(item);

      // Keep each row's own displayed unit price in sync with the current VAT
      // choice - this used to be static Liquid-rendered markup that never
      // updated after the customer picked "excl. VAT", showing the wrong
      // (tax-inclusive) number forever. Refresh it every cycle regardless of
      // quantity, since it should reflect the choice even before adding any.
      const priceEl = item.querySelector('.bb-variant-price');
      if (priceEl) priceEl.textContent = this.formatMoney(this.getDisplayCents(price));

      if (qty > 0) {
        items.push({ id: parseInt(item.dataset.variantId, 10), quantity: qty });
        rawSubtotal += price * qty;
        totalQty += qty;
        item.classList.add('has-quantity');
      } else {
        item.classList.remove('has-quantity');
      }
    });

    // What the tier bar and discount tier should be based on: everything of
    // this product already in the cart, plus what's selected right now.
    const barQty = totalQty + (this.cartQuantity || 0);
    const tier = this.getActiveTier(barQty);
    // Tier/discount math stays in raw (tax-inclusive) cents throughout - this
    // is the real amount that gets added to the cart via /cart/add.js and must
    // match Shopify's own recorded prices. Only the final totals are converted
    // for display, exactly like every other price surface the VAT toggle touches.
    const discountedSubtotal = rawSubtotal * (1 - tier.discount / 100);
    const displaySubtotal = this.getDisplayCents(discountedSubtotal);

    if (this.totalQtyEl) this.totalQtyEl.textContent = totalQty;
    if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(displaySubtotal);

    if (this.addButton) {
      if (totalQty === 0) {
        this.addButton.setAttribute('disabled', 'disabled');
        if (this.buttonText) this.buttonText.textContent = this.addButton.dataset.labelEmpty;
      } else {
        this.addButton.removeAttribute('disabled');
        if (this.buttonText) {
          this.buttonText.textContent = `${this.addButton.dataset.labelReady} • ${this.formatMoney(displaySubtotal)}`;
        }
      }
    }

    this.updateTierProgress(barQty, tier);
    this.container.dataset.bundleItems = JSON.stringify(items);
  }

  updateTierProgress(totalQty, activeTier) {
    this.tierEls.forEach((el) => {
      const tierQty = parseInt(el.dataset.tierQty, 10);
      el.classList.toggle('is-active', tierQty === activeTier.qty);
    });

    if (!this.progressFill) return;

    const n = this.tiers.length;
    if (n < 1) {
      this.progressFill.style.width = '0%';
      return;
    }

    // Each tier dot sits at the horizontal center of its flex segment
    // (n equal-width segments), matching the CSS layout in
    // component-bundle-builder.css (.bb-tier { flex: 1 }).
    const dotPosition = (i) => ((i + 0.5) / n) * 100;

    // Interpolation anchors: a virtual (qty: 0, pos: 0%) starting point so
    // the bar reads as genuinely empty when nothing's in the cart/selection,
    // followed by each real tier dot at its flex-layout position. Without
    // this virtual point the fill always sat at dot 0's position (e.g. 10%)
    // even at qty 0, making the bar look like it already had progress.
    const points = [{ qty: 0, pos: 0 }, ...this.tiers.map((t, i) => ({ qty: t.qty, pos: dotPosition(i) }))];

    // Find which pair of points totalQty currently sits between, so we can
    // interpolate the fill smoothly toward the next tier instead of
    // snapping straight to it.
    let segmentStart = 0;
    for (let i = 0; i < points.length - 1; i++) {
      if (totalQty >= points[i + 1].qty) segmentStart = i + 1;
    }

    if (segmentStart >= points.length - 1) {
      // At or past the final tier: fill goes all the way to the last dot.
      this.progressFill.style.width = `${points[points.length - 1].pos}%`;
      return;
    }

    const from = points[segmentStart];
    const to = points[segmentStart + 1];
    const span = to.qty - from.qty;
    const segmentProgress = span > 0
      ? Math.min(1, Math.max(0, (totalQty - from.qty) / span))
      : 0;

    const width = from.pos + (to.pos - from.pos) * segmentProgress;

    this.progressFill.style.width = `${width}%`;
  }

  resetBundle() {
    this.variantItems.forEach((item) => {
      const input = item.querySelector('[data-qty-input]');
      const addBtn = item.querySelector('[data-variant-add]');
      const wrapper = item.querySelector('[data-quantity-wrapper]');
      input.value = 0;
      wrapper.style.display = 'none';
      addBtn.style.display = 'inline-block';
      item.classList.remove('has-quantity');
    });
    this.updateBundle();
  }

  async addToCart() {
    const itemsData = this.container.dataset.bundleItems;
    if (!itemsData) return;
    const items = JSON.parse(itemsData);
    if (!items.length) return;

    const addedQty = items.reduce((sum, item) => sum + item.quantity, 0);

    const labelAdding = this.addButton.dataset.labelAdding;
    this.addButton.setAttribute('disabled', 'disabled');
    this.addButton.classList.add('loading');
    if (this.buttonText) this.buttonText.textContent = labelAdding;

    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { items };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }

    try {
      const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      const response = await fetch(`${cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.description || data.message || 'Något gick fel, försök igen.');
        return;
      }

      // Optimistic update so the bar doesn't flash back to its old position
      // while syncCartQuantity()'s /cart.js round-trip is in flight - the
      // cartUpdate subscription above will correct this shortly after with
      // the authoritative cart state anyway.
      this.cartQuantity = (this.cartQuantity || 0) + addedQty;
      this.resetBundle();

      // Dawn's own product-form.js explicitly clears `is-empty` on the
      // outer <cart-drawer> element after a successful add (separately
      // from renderContents(), which only touches the inner .drawer__inner
      // wrapper). /cart/add.js never returns an `item_count` field - for
      // multi-item batch adds it only returns { items, sections } - so any
      // logic that gates on item_count from this response can't be relied
      // on here. Without this line, adding to a previously-empty cart opens
      // the drawer still showing its empty-state markup even though the
      // item was actually added.
      if (cart) {
        cart.classList.remove('is-empty');
      }

      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(data);
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-builder',
          cartData: data,
        });
      }

      document.dispatchEvent(new CustomEvent('bundle-builder:added', { detail: { cart: data } }));
    } catch (e) {
      console.error(e);
      alert('Något gick fel, försök igen.');
    } finally {
      this.addButton.classList.remove('loading');
      this.updateBundle();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-bundle-builder]').forEach((el) => new BundleBuilder(el));
});
